import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { AgentSheet } from '../components/workspace/AgentSheet'
import { Canvas, type CanvasMode } from '../components/workspace/Canvas'
import { CameraSheet } from '../components/workspace/CameraSheet'
import { CartSheet } from '../components/workspace/CartSheet'
import { Filmstrip } from '../components/workspace/Filmstrip'
import { ProductRail } from '../components/workspace/ProductRail'
import { RoomTabs } from '../components/workspace/RoomTabs'
import { ShareSheet } from '../components/workspace/ShareSheet'
import { SlotChips } from '../components/workspace/SlotChips'
import { useQueryClient } from '@tanstack/react-query'

import type { UIAction } from '../lib/agent'
import { api, ApiError } from '../lib/api'
import { prettyLabel } from '../lib/labels'
import { segmenter } from '../lib/segment'
import {
  sessionKey,
  useAddCartLine,
  useAddMask,
  useAddPhoto,
  useCreateRender,
  useCreateShare,
  useCustomRequest,
  useDeleteCartLine,
  useDeleteMyData,
  useDeleteMask,
  useProductSearch,
  useRenderStream,
  useSendToAndrew,
  useSlotCoverage,
  useSlots,
  useUpdateCartLine,
  useUpdateMask,
  useWorkspace,
} from '../lib/workspace'
import type { ShareLink } from '../lib/workspace'
import type { ProductCard, WorkspaceRender } from '../lib/wstypes'

/**
 * The design studio (01 §3.5).
 *
 * The shape of the screen is the shape of the job: **your room on the left, real products on
 * the right, the list underneath.** Tap something in the photo, pick a product, see it there.
 *
 * Three decisions hold the whole thing together:
 *
 *  * **One selection at a time is "active".** The canvas, the chips and the rail all read from
 *    it. A workspace where the rail is showing vanities while the canvas has a mirror selected
 *    is a workspace that will put a mirror in the vanity line.
 *  * **"Try it" is a render, not a preview swap.** It costs money and takes ten seconds, so it
 *    is explicit, it is streamed, and the count of what is left is visible. Nobody is surprised
 *    by running out.
 *  * **"Add to list" is separate from "Try it".** 01 §3.5 has people add things they never
 *    rendered and render things they never add. Forcing them through one flow would lose both.
 */

export function Workspace() {
  const { sessionId = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()

  /**
   * The key from the link (01 §3.1 — "no account").
   *
   * A workspace link only ever arrives by email, and email hands it to whichever browser is
   * default — which is routinely not the one the intake form was filled in on. That browser has
   * no session cookie, so every one of those taps used to be a 401 and a dead end.
   *
   * The link carries its own credential now. Exchange it for a cookie, then take it out of the
   * address bar so it does not end up in a screenshot, a shared URL or the back stack.
   */
  const [claiming, setClaiming] = useState(() => Boolean(params.get('k')))
  useEffect(() => {
    const key = params.get('k')
    if (!key) return
    let cancelled = false
    void api
      .post(`/workspace/sessions/${sessionId}/claim`, { key })
      .catch(() => {})
      .finally(() => {
        if (cancelled) return
        const next = new URLSearchParams(params)
        next.delete('k')
        setParams(next, { replace: true })
        setClaiming(false)
        void queryClient.invalidateQueries({ queryKey: sessionKey(sessionId) })
      })
    return () => {
      cancelled = true
    }
    // Deliberately only on mount: `params` changes as soon as the key is stripped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])
  const session = useWorkspace(sessionId)
  const slotsQuery = useSlots()
  const coverageQuery = useSlotCoverage()

  const [roomId, setRoomId] = useState<string | null>(null)
  const [activeMaskId, setActiveMaskId] = useState<string | null>(null)
  const [mode, setMode] = useState<CanvasMode>('browse')
  //: An auto mask the homeowner says is mislabelled — shows the slot chips for it once.
  const [reslotId, setReslotId] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [cartOpen, setCartOpen] = useState(params.get('list') === '1')
  const [cameraOpen, setCameraOpen] = useState(false)
  const [agentOpen, setAgentOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const [shareLink, setShareLink] = useState<ShareLink | null>(null)
  const [activeRender, setActiveRender] = useState<WorkspaceRender | null>(null)
  const [pendingRenderId, setPendingRenderId] = useState<string | null>(null)
  const [compare, setCompare] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const canvasColumnRef = useRef<HTMLDivElement>(null)

  const addPhoto = useAddPhoto(sessionId)
  const addMask = useAddMask(sessionId)
  const updateMask = useUpdateMask(sessionId)
  const deleteMask = useDeleteMask(sessionId)
  const createRender = useCreateRender(sessionId)
  const addLine = useAddCartLine(sessionId)
  const updateLine = useUpdateCartLine(sessionId)
  const deleteLine = useDeleteCartLine(sessionId)
  const customRequest = useCustomRequest(sessionId)
  const send = useSendToAndrew(sessionId)
  const createShare = useCreateShare(sessionId)
  const stream = useRenderStream(sessionId, pendingRenderId)

  const data = session.data
  const rooms = useMemo(() => data?.rooms ?? [], [data?.rooms])
  const eraseMe = useDeleteMyData(data?.estimate_request_id ?? '')
  const room = useMemo(
    () => rooms.find((entry) => entry.id === roomId) ?? rooms[0] ?? null,
    [rooms, roomId],
  )
  const basePhoto = room?.photos.find((photo) => photo.is_base) ?? room?.photos[0] ?? null
  const masks = useMemo(() => room?.masks ?? [], [room?.masks])
  const activeMask = masks.find((mask) => mask.id === activeMaskId) ?? null
  const slots = slotsQuery.data ?? []
  const activeSlot = activeMask?.slot ? slots.find((s) => s.id === activeMask.slot) : undefined

  const search = useProductSearch(
    { slot: activeMask?.slot ?? null, q: query || undefined },
    Boolean(activeMask?.slot) || query.length > 1,
  )

  useEffect(() => {
    if (!roomId && rooms[0]) setRoomId(rooms[0].id)
  }, [rooms, roomId])

  // Leaving the studio releases the segmentation worker and its weights. The model runtime is
  // by far the biggest allocation in the app, and a phone that navigates to the cart-less
  // pages (privacy, shared previews) or back to the confirmation should not keep paying for
  // it — memory pressure is what gets a mobile tab killed and reloaded.
  useEffect(() => {
    return () => segmenter.dispose()
  }, [])

  // With one selection in the room, open on it. With several (the detected items), open calm:
  // the person taps the one they care about rather than being handed an arbitrary vanity.
  useEffect(() => {
    if (activeMaskId || masks.length !== 1) return
    const only = masks[0]
    if (only?.slot_confirmed) setActiveMaskId(only.id)
  }, [masks, activeMaskId])

  // Detection runs for a minute or two after a photo lands; poll the session while it does so
  // the chips appear without anyone refreshing. Stops itself: masks arriving or the photo
  // ageing out both end the interval.
  const detecting = Boolean(
    basePhoto &&
    !masks.some((mask) => mask.photo_id === basePhoto.id) &&
    Date.now() - new Date(basePhoto.created_at).getTime() < 90_000,
  )
  useEffect(() => {
    if (!detecting) return
    const timer = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: sessionKey(sessionId) })
    }, 4000)
    return () => clearInterval(timer)
  }, [detecting, queryClient, sessionId])

  // When a render finishes, show it. The stream invalidates the session; this picks the row
  // back up once it has landed, so the canvas flips to the result without a second click.
  useEffect(() => {
    if (stream.status !== 'done' || !pendingRenderId) return
    const finished = rooms
      .flatMap((entry) => entry.renders)
      .find((render) => render.id === pendingRenderId)
    if (finished?.status === 'done') {
      setActiveRender(finished)
      setPendingRenderId(null)
      setCompare(true)
      // On a phone, tapping "Try it" scrolled the rail into view, so ten seconds later the
      // finished preview lands above the viewport and nothing announces it. Bring the canvas
      // back; `nearest` makes this a no-op anywhere it is already on screen.
      canvasColumnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [stream.status, pendingRenderId, rooms])

  useEffect(() => {
    if (stream.status === 'failed') {
      setNotice(stream.error)
      setPendingRenderId(null)
    }
  }, [stream.status, stream.error])

  if (claiming || session.isPending) {
    return <Centered>Opening your workspace…</Centered>
  }
  if (session.isError || !data) {
    // 401 and 404 are different problems and used to produce the same dead end: "Open it again
    // from the link Andrew emailed you" — which is what the person had just done. The common
    // case by far is 401: the workspace link only ever arrives by email, Mail hands it to
    // whichever browser is default, and a browser that never signed in has no cookie. There is
    // always a way forward from here now.
    const notSignedIn = session.error instanceof ApiError && session.error.problem.status === 401
    return (
      <Centered>
        {notSignedIn ? (
          <>
            <p className="m-0 text-lg font-semibold text-ink">
              You are not signed in on this phone.
            </p>
            <p className="m-0 mt-2 max-w-[42ch] text-sm text-muted">
              Links open in whichever browser your email app uses, so this one may simply not
              have been the one you started in.
            </p>
            <Link
              to={`/resume?next=${encodeURIComponent(`/w/${sessionId}`)}`}
              className="mt-5 inline-block rounded-btn bg-blue px-7 py-3.5 font-bold text-white shadow-blue"
            >
              Email me a link to get back in
            </Link>
          </>
        ) : (
          <>
            <p className="m-0 text-lg font-semibold text-ink">We could not open that workspace.</p>
            <p className="m-0 mt-2 max-w-[42ch] text-sm text-muted">
              The link may have been for a session that has since been closed.
            </p>
            <Link
              to="/resume"
              className="mt-5 inline-block rounded-btn bg-blue px-7 py-3.5 font-bold text-white shadow-blue"
            >
              Find my room
            </Link>
          </>
        )}
      </Centered>
    )
  }

  const readOnly = data.status !== 'active'
  const rendersLeft = data.budget.renders_allowed - data.budget.renders_used

  /**
   * Add a photo, and let the caller know whether it landed.
   *
   * The file-picker path wants a notice; the camera sheet wants to report inside itself, because
   * it is full-screen and the notice bar is behind it. So this reports *and* rethrows, and the
   * camera awaits it.
   */
  async function onPhotoPicked(file: Blob, source: 'upload' | 'camera_capture' | 'camera_pin') {
    if (!room) throw new Error('Pick a room first.')
    try {
      await addPhoto.mutateAsync({ roomId: room.id, file, source })
      setActiveRender(null)
      setCompare(false)
    } catch (error) {
      const message =
        error instanceof ApiError
          ? (error.problem.detail ?? error.problem.title)
          : 'That photo did not upload.'
      setNotice(message)
      throw new Error(message)
    }
  }

  async function tryProduct(product: ProductCard) {
    if (!room || !activeMask || !basePhoto) return
    setNotice(null)
    try {
      const render = await createRender.mutateAsync({
        room_id: room.id,
        base_photo_id: basePhoto.id,
        selections: [{ mask_id: activeMask.id, product_id: product.id }],
      })
      setPendingRenderId(render.id)
      setActiveRender(null)
    } catch (error) {
      setNotice(
        error instanceof ApiError
          ? (error.problem.detail ?? error.problem.title)
          : 'That preview could not be started.',
      )
    }
  }

  /**
   * What the agent asked the app to do (04 §2 "respond").
   *
   * The agent and the buttons drive the same state — "show me black faucets" fills the same
   * rail the slot chips fill. A chat that could only talk would be a second product bolted
   * onto the first.
   */
  function applyActions(actions: UIAction[]) {
    for (const action of actions) {
      if (action.type === 'show_rail' && action.slot) {
        const match = masks.find((mask) => mask.slot === action.slot)
        if (match) setActiveMaskId(match.id)
        setQuery('')
      } else if (action.type === 'start_render' && action.render_id) {
        setPendingRenderId(action.render_id)
        setAgentOpen(false)
      } else if (action.type === 'open_cart') {
        setCartOpen(true)
      }
    }
  }

  async function addProduct(product: ProductCard) {
    if (!room) return
    await addLine.mutateAsync({
      room_id: room.id,
      slot: activeMask?.slot ?? product.slots[0] ?? null,
      product_id: product.id,
      render_id: activeRender?.id ?? null,
    })
    setNotice(`${product.name} added to your list.`)
  }

  return (
    <div className="mx-auto flex min-h-[calc(100dvh-4rem)] w-full max-w-[1400px] flex-col overflow-x-clip px-5 pb-[calc(var(--safe-bottom)+5.5rem)] pt-4 lg:pb-8 lg:pt-6">
      {/* Compact on a phone, deliberately.
          This screen's whole proposition is "your room, and you can touch it", and the first
          build put an eyebrow, a 30px serif headline and three lines of body copy above the
          photo — so on an iPhone 13 the room began at 70 % of the viewport and a homeowner
          opening the studio saw no room at all until they scrolled. A landing page earns that
          header. A tool does not. The orientation the paragraph was carrying is given by the
          canvas hint underneath the photo, at the moment it is useful; what stays on a phone is
          the 01 §7 sentence, which is not decoration. */}
      <header className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        {/* One functional name, nothing else: the tagline and the header disclaimer were the
            owner's own examples of copy that says nothing (2026-08-23). Pricing honesty lives
            where prices appear — the cart and the printed packet — not as wallpaper. */}
        <h1 className="text-2xl lg:text-3xl">Design studio</h1>
        {/* Magic-link sessions get opened on shared and borrowed phones; leaving one signed in
            was the tracker's known gap. Everything is saved server-side, so signing out costs
            nothing and the email link brings it all back. */}
        <button
          type="button"
          onClick={async () => {
            try {
              await api.post('/auth/logout')
            } catch {
              // The cookie may already be gone; either way the session is not usable here.
            }
            navigate('/')
          }}
          className="min-h-11 rounded-btn border border-line bg-white px-3.5 text-xs font-semibold text-muted"
        >
          Sign out
        </button>
      </header>

      {readOnly && (
        <p className="mt-4 rounded-card border border-line bg-sand px-4 py-3 text-sm text-ink">
          {data.status === 'sent'
            ? 'This has been sent to Andrew. You can still look at everything you chose.'
            : 'This workspace has expired, but everything you chose is saved with Andrew.'}
        </p>
      )}

      <div className="mt-3 lg:mt-5">
        <RoomTabs
          rooms={rooms}
          activeId={room?.id ?? null}
          onSelect={(id) => {
            setRoomId(id)
            setActiveMaskId(null)
            setActiveRender(null)
          }}
          onAdd={() => setNotice('Ask Andrew to add another room when he visits.')}
        />
      </div>

      <div className="mt-3 grid flex-1 gap-6 lg:mt-5 lg:grid-cols-[minmax(0,1.55fr)_minmax(320px,0.85fr)]">
        {/* ------------------------------------------------------------------ canvas */}
        <div ref={canvasColumnRef} className="flex min-h-[58dvh] min-w-0 flex-col lg:min-h-[52vh]">
          {basePhoto ? (
            <>
              {/* One quiet row. The default mode needs no tools at all — the items in the
                  photo are the buttons — so what remains is before/after, the escape hatch for
                  something detection missed, and the camera. Scrolled rather than wrapped so
                  nothing steals a second line from the photo on a phone. */}
              <div className="-mx-5 flex items-center gap-2 overflow-x-auto px-5 pb-1 sm:mx-0 sm:flex-wrap sm:px-0">
                {activeRender && (
                  <ToolButton active={compare} onClick={() => setCompare((value) => !value)}>
                    {compare ? 'Hide before/after' : 'Before / after'}
                  </ToolButton>
                )}
                {mode === 'browse' ? (
                  <ToolButton active={false} onClick={() => setMode('brush')}>
                    Missed something?
                  </ToolButton>
                ) : (
                  <>
                    <ToolButton active={mode === 'brush'} onClick={() => setMode('brush')}>
                      Paint it
                    </ToolButton>
                    <ToolButton active={mode === 'erase'} onClick={() => setMode('erase')}>
                      Erase
                    </ToolButton>
                    <ToolButton active={false} onClick={() => setMode('browse')}>
                      Done
                    </ToolButton>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setCameraOpen(true)}
                  className="ml-auto min-h-11 shrink-0 rounded-btn border border-line bg-white px-4 text-sm font-semibold text-blue-deep"
                >
                  Use camera
                </button>
              </div>

              <div className="mt-3 min-h-[46dvh] flex-1">
                <Canvas
                  photo={basePhoto}
                  masks={masks}
                  activeMaskId={activeMaskId}
                  mode={mode}
                  overlayUrl={activeRender?.url ?? null}
                  compare={compare}
                  detecting={detecting}
                  rendering={Boolean(pendingRenderId)}
                  disabled={readOnly || Boolean(pendingRenderId)}
                  onSelectMask={setActiveMaskId}
                  onMaskDrawn={async (payload) => {
                    if (!room) return
                    const mask = await addMask.mutateAsync({
                      roomId: room.id,
                      photo_id: basePhoto.id,
                      png_base64: payload.png_base64,
                      method: payload.method,
                    })
                    setActiveMaskId(mask.id)
                    setMode('browse')
                    setActiveRender(null)
                    setCompare(false)
                  }}
                />
              </div>

              {activeRender && (
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <p className="m-0 text-xs text-muted">
                    {activeRender.disclaimer}
                    {activeRender.low_confidence && ' · this preview may be imperfect'}
                  </p>
                  <button
                    type="button"
                    onClick={async () => {
                      setShareOpen(true)
                      setShareLink(null)
                      try {
                        setShareLink(await createShare.mutateAsync(activeRender.id))
                      } catch {
                        setShareOpen(false)
                        setNotice('That link could not be made. Try again in a moment.')
                      }
                    }}
                    className="ml-auto min-h-11 rounded-btn border border-line bg-white px-3.5 text-xs font-semibold text-blue-deep"
                  >
                    Share this preview
                  </button>
                </div>
              )}

              <Filmstrip
                renders={room?.renders ?? []}
                activeId={activeRender?.id ?? null}
                onSelect={(render) => {
                  setActiveRender(render)
                  setCompare(true)
                }}
              />
            </>
          ) : (
            <EmptyCanvas
              onUpload={() => fileRef.current?.click()}
              onCamera={() => setCameraOpen(true)}
              busy={addPhoto.isPending}
            />
          )}
        </div>

        {/* -------------------------------------------------------------------- rail */}
        <aside className="flex min-h-0 min-w-0 flex-col gap-4">
          {pendingRenderId && (
            <div className="anim-rise rounded-card border border-blue bg-blue-tint p-4">
              <p className="m-0 text-sm font-semibold text-ink">Making your preview…</p>
              <p className="m-0 mt-1 text-xs text-muted">
                Usually about ten seconds. You can keep browsing while it works.
              </p>
            </div>
          )}

          {activeMask && (!activeMask.slot_confirmed || reslotId === activeMask.id) && (
            <SlotChips
              mask={activeMask}
              slots={slots}
              category={room?.category ?? 'bathroom'}
              coverage={coverageQuery.data ?? {}}
              onConfirm={(slot) => {
                setReslotId(null)
                void updateMask.mutateAsync({ maskId: activeMask.id, slot })
              }}
              onDelete={() => {
                setReslotId(null)
                void deleteMask.mutateAsync(activeMask.id)
                setActiveMaskId(null)
              }}
            />
          )}

          {activeMask?.slot_confirmed && reslotId !== activeMask.id && !readOnly && (
            <button
              type="button"
              onClick={() => setReslotId(activeMask.id)}
              className="inline-flex min-h-11 items-center self-start text-xs text-muted underline underline-offset-2"
            >
              Not a {prettyLabel(activeSlot?.name ?? activeMask.label ?? 'match').toLowerCase()}?
              Fix it
            </button>
          )}
          {activeMask?.slot_confirmed || query ? (
            <ProductRail
              slot={activeMask?.slot ?? null}
              slotName={activeSlot?.name ?? null}
              products={search.data?.products ?? []}
              loading={search.isFetching}
              zeroReason={search.data?.zero_result_reason ?? null}
              activeProductId={null}
              query={query}
              disabled={readOnly || Boolean(pendingRenderId)}
              onSearch={setQuery}
              onTry={(product) => void tryProduct(product)}
              onAdd={(product) => void addProduct(product)}
              onCustom={() => {
                const description = window.prompt('What do you have in mind?')
                if (description && room) {
                  void customRequest.mutateAsync({
                    room_id: room.id,
                    slot: activeMask?.slot ?? null,
                    description,
                  })
                }
              }}
            />
          ) : (
            <div className="rounded-card border border-line bg-white p-5">
              <h2 className="text-lg">
                {detecting ? 'Finding what’s in your photo…' : 'Tap an item in your photo'}
              </h2>
            </div>
          )}
        </aside>
      </div>

      {/* -------------------------------------------------------------- bottom bar */}
      {/* Opaque, not translucent: this bar sits over the page footer on a phone, and 95 %
          white let two paragraphs of unrelated text read through each other. */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-white px-5 pb-[calc(var(--safe-bottom)+0.75rem)] pt-3 shadow-[0_-8px_24px_-16px_rgb(16_34_46_/_0.4)] lg:static lg:mt-6 lg:rounded-card lg:border lg:shadow-none">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-x-3 gap-y-2">
          <button
            type="button"
            onClick={() => setAgentOpen(true)}
            className="min-h-12 shrink-0 whitespace-nowrap rounded-btn border border-line bg-white px-4 font-semibold text-blue-deep sm:px-5"
          >
            Ask
          </button>
          <button
            type="button"
            onClick={() => setCartOpen(true)}
            className="min-h-12 shrink-0 whitespace-nowrap rounded-btn bg-navy px-4 font-semibold text-white sm:px-5"
          >
            Your list
            {data.cart.lines.length > 0 && (
              <span className="ml-2 rounded-pill bg-white/20 px-2 py-0.5 text-xs">
                {data.cart.lines.length}
              </span>
            )}
          </button>
          {/* Its own line on a phone. Sharing the row with two buttons truncated it to
              "12 previews left in this sess…" — a number whose whole job is to stop the
              limit being a surprise. */}
          <p className="m-0 w-full min-w-0 text-xs text-muted sm:ml-auto sm:w-auto sm:flex-1 sm:truncate sm:text-right">
            {rendersLeft > 0
              ? `${rendersLeft} preview${rendersLeft === 1 ? '' : 's'} left in this session`
              : 'You have used this session’s previews — your list is saved'}
          </p>
        </div>
      </div>

      {notice && (
        <div
          role="status"
          className="fixed inset-x-5 bottom-[calc(var(--safe-bottom)+5.5rem)] z-40 mx-auto max-w-md rounded-card bg-navy px-4 py-3 text-sm text-white shadow-lift lg:bottom-6"
        >
          {notice}
          {/* The gremlin's first catch (2026-08-23): this was a bare text button, 21x20px —
              a quarter of a fingertip. The padding and negative margin buy a 44px target
              without making the toast taller. */}
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="-my-3 ml-1 min-h-11 min-w-11 px-3 py-3 font-semibold underline"
          >
            OK
          </button>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          // The notice bar is this path's feedback; the rethrow is for the camera sheet.
          if (file) void onPhotoPicked(file, 'upload').catch(() => {})
          event.target.value = ''
        }}
      />

      <CameraSheet
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={(blob, source) => onPhotoPicked(blob, source)}
      />

      <AgentSheet
        sessionId={sessionId}
        open={agentOpen}
        onClose={() => setAgentOpen(false)}
        roomId={room?.id ?? null}
        maskId={activeMaskId}
        onActions={applyActions}
        disabled={readOnly}
      />

      <ShareSheet
        open={shareOpen}
        link={shareLink}
        busy={createShare.isPending}
        onClose={() => setShareOpen(false)}
      />

      <CartSheet
        cart={data.cart}
        open={cartOpen}
        readOnly={readOnly}
        onClose={() => setCartOpen(false)}
        onQty={(lineId, qty) => void updateLine.mutateAsync({ lineId, qty })}
        onRemove={(lineId) => void deleteLine.mutateAsync(lineId)}
        onDeleteMyData={async () => {
          if (
            !window.confirm(
              'Delete everything? Your photos, previews and messages are removed within a few ' +
                'minutes. This cannot be undone.',
            )
          ) {
            return
          }
          try {
            const result = await eraseMe.mutateAsync()
            setCartOpen(false)
            setNotice(result.message)
          } catch {
            setNotice(
              'That did not go through. Email studio@asbhomerenovations.com and we will do it.',
            )
          }
        }}
        sending={send.isPending}
        onSend={async () => {
          try {
            await send.mutateAsync()
            setCartOpen(false)
            setNotice(
              "Sent. Andrew has your list — he'll price it after the visit, and you'll get a " +
                'copy by email.',
            )
          } catch (error) {
            setNotice(
              error instanceof ApiError
                ? (error.problem.detail ?? error.problem.title)
                : 'That did not send. Try again in a moment.',
            )
          }
        }}
      />
    </div>
  )
}

function ToolButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      // `shrink-0` and `whitespace-nowrap` because the row scrolls rather than wraps: without
      // them flex shrinks the chips to fit and "Tap to select" breaks over three lines.
      className={`min-h-11 shrink-0 whitespace-nowrap rounded-pill border px-4 text-sm font-semibold transition-colors ${
        active
          ? 'border-navy bg-navy text-white'
          : 'border-line bg-white text-ink hover:border-blue'
      }`}
    >
      {children}
    </button>
  )
}

function EmptyCanvas({
  onUpload,
  onCamera,
  busy,
}: {
  onUpload: () => void
  onCamera: () => void
  busy: boolean
}) {
  return (
    <div className="grid flex-1 place-items-center rounded-img border border-dashed border-line-2 bg-white p-8 text-center">
      <div className="max-w-sm">
        <h2 className="text-xl">Start with a photo of the room</h2>
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={onCamera}
            className="min-h-12 rounded-btn bg-blue px-5 font-semibold text-white shadow-blue"
          >
            Use camera
          </button>
          <button
            type="button"
            onClick={onUpload}
            disabled={busy}
            className="min-h-12 rounded-btn border border-line bg-white px-5 font-semibold text-ink disabled:opacity-50"
          >
            {busy ? 'Uploading…' : 'Upload a photo'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto grid min-h-[60vh] max-w-md place-items-center px-5 text-center">
      <div>{children}</div>
    </div>
  )
}
